import {
  BillingInformationModel,
  StripeCreditCardPaymentSourceModel,
  StripePaymentSourceModel,
} from '../../api/billing-types';
import Visa from '../../assets/cards/visa.png';
import Amex from '../../assets/cards/amex.png';
import Mastercard from '../../assets/cards/mastercard.png';
import Discover from '../../assets/cards/discover.png';
import { HubSpotCompany } from '../../api/billing-types';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetValues,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { CardElement } from '@stripe/react-stripe-js';
import { states, countryOptions } from '../../shared/stripe-address-utils';
import PoweredByStripe from '../../assets/powered-by-stripe.svg';
import PoweredByStripeWhite from '../../assets/powered-by-stripe-white.svg';
import { CheckoutModelSchema } from './billing-form-page';

import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import { styled, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AccountBalance from '@mui/icons-material/AccountBalance';
import Close from '@mui/icons-material/Close';
import CloudUpload from '@mui/icons-material/CloudUpload';
import CreditCard from '@mui/icons-material/CreditCard';
import Description from '@mui/icons-material/Description';
import Receipt from '@mui/icons-material/Receipt';

interface ChoosePaymentMethodStepProps {
  billingInfoModel: BillingInformationModel;
  hubSpotCompany: HubSpotCompany;
  control: Control<CheckoutModelSchema>;
  watch: UseFormWatch<CheckoutModelSchema>;
  setValue: UseFormSetValue<CheckoutModelSchema>;
  errors: FieldErrors<CheckoutModelSchema>;
  getValues: UseFormGetValues<CheckoutModelSchema>;
  trigger: UseFormTrigger<CheckoutModelSchema>;
}

export const ChoosePaymentMethodStep = ({
  billingInfoModel,
  hubSpotCompany,
  control,
  setValue,
  errors,
  watch,
  getValues,
  trigger,
}: ChoosePaymentMethodStepProps) => {
  const theme = useTheme();

  function isCreditCard(
    paymentMethod: StripePaymentSourceModel
  ): paymentMethod is StripeCreditCardPaymentSourceModel {
    return paymentMethod.stripePaymentSourceType === 'CreditCard';
  }

  const creditCardPaymentsEnabled =
    hubSpotCompany.properties.credit_card_payments_enabled;
  const poEnabled = hubSpotCompany.properties.enable_offline_purchasing;

  const images = {
    Visa: Visa,
    'American Express': Amex,
    MasterCard: Mastercard,
    Discover: Discover,
  };

  const stripePaymentType = watch('stripePaymentType');
  const selectedCountry = watch('newStripeCreditCard.address_country');

  const addingNewCreditCard =
    getValues('stripePaymentMethodId') === 'NewStripeCreditCard';

  const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
  });

  const purchaseOrderUpload = watch('purchaseOrderUpload');

  const selectedPaymentMethodId = watch('stripePaymentMethodId');
  const selectedMethod = billingInfoModel.stripeSourceModels.find(
    (method) => method.id === selectedPaymentMethodId
  );

  const showAchAgreement =
    stripePaymentType === 'StripeBankAccount' &&
    String(selectedMethod?.metadata?.hasMultiUseMandate) !== 'true';
  return (
    <Stack flexDirection="column" gap={2}>
      <Stack flexDirection="row" gap={2}>
        <Controller
          name={'stripePaymentMethodId'}
          control={control}
          render={({ field }) => (
            <FormControl sx={{ pl: 4 }}>
              <RadioGroup
                value={field.value}
                onChange={(e) => {
                  field.onChange(e.target.value);

                  if (e.target.value === 'NewStripeCreditCard') {
                    setValue('stripePaymentType', 'StripeCreditCard');
                  } else {
                    setValue(
                      'stripePaymentType',
                      e.target.value === 'Invoice'
                        ? 'Invoice'
                        : e.target.value === 'PurchaseOrder'
                        ? 'PurchaseOrder'
                        : isCreditCard(
                            billingInfoModel.stripeSourceModels.find(
                              (method) => method.id === e.target.value
                            )!
                          )
                        ? 'StripeCreditCard'
                        : 'StripeBankAccount'
                    );
                  }
                  setValue('purchaseOrderNumber', '');
                  setValue('purchaseOrderUpload', null);
                  setValue('newStripeCreditCard.account_number', '');
                  setValue('newStripeCreditCard.account_holder_name', '');
                  setValue('newStripeCreditCard.address_city', '');
                  setValue('newStripeCreditCard.address_country', '');
                  setValue('newStripeCreditCard.address_line1', '');
                  setValue('newStripeCreditCard.address_state', '');
                  setValue('newStripeCreditCard.address_zip', '');
                  setValue('newStripeCreditCard.bank_name', '');
                  setValue('newStripeCreditCard.brand', '');
                  setValue('newStripeCreditCard.cardDetails2String', '');
                  setValue('newStripeCreditCard.cardDetailsString', '');
                }}
                sx={{
                  display: 'flex',
                  gap: 1,
                }}
              >
                <FormControlLabel
                  value="Invoice"
                  control={<Radio />}
                  label={
                    <Stack flexDirection="row" gap={1} alignItems="center">
                      <Receipt />
                      <Typography>Invoice</Typography>
                    </Stack>
                  }
                />
                {poEnabled && (
                  <FormControlLabel
                    value="PurchaseOrder"
                    control={<Radio />}
                    label={
                      <Stack flexDirection="row" gap={1} alignItems="center">
                        <Description />
                        <Typography>Purchase Order</Typography>
                      </Stack>
                    }
                  />
                )}
                {creditCardPaymentsEnabled && (
                  <FormControlLabel
                    value="NewStripeCreditCard"
                    control={<Radio />}
                    label={
                      <Stack flexDirection="row" gap={1} alignItems="center">
                        <CreditCard />
                        <Typography>Pay with Card</Typography>
                      </Stack>
                    }
                  />
                )}
                {billingInfoModel.stripeSourceModels.map((method) => {
                  const isCreditCardMethod = isCreditCard(method);
                  const achNeedsVerification = method.showWarning;
                  const creditCardPaymentsDisabled =
                    !creditCardPaymentsEnabled && isCreditCardMethod;

                  return (
                    <Tooltip
                      title={
                        creditCardPaymentsDisabled
                          ? 'Credit card payments are disabled'
                          : achNeedsVerification
                          ? 'Needs verification'
                          : ''
                      }
                      arrow
                      key={method.id}
                    >
                      <FormControlLabel
                        value={method.id}
                        control={
                          <Radio
                            disabled={
                              achNeedsVerification || creditCardPaymentsDisabled
                            }
                          />
                        }
                        label={
                          <Stack
                            flexDirection="row"
                            gap={1}
                            alignItems="center"
                          >
                            <Avatar
                              src={images[method.brand as keyof typeof images]}
                              variant="rounded"
                              sx={{
                                '&.Mui-disabled': {
                                  filter: 'grayscale(100%)',
                                  opacity: 0.5,
                                },
                              }}
                            >
                              <AccountBalance />
                            </Avatar>
                            {isCreditCardMethod ? (
                              <Typography>{`${method.brand} - ${method.last4}`}</Typography>
                            ) : (
                              <Typography>
                                {method.bank_name} - {method.last4}
                              </Typography>
                            )}
                            {method.showWarning && (
                              <Typography variant="caption" sx={{ ml: 0.5 }}>
                                (Verification required)
                              </Typography>
                            )}
                          </Stack>
                        }
                      />
                    </Tooltip>
                  );
                })}
              </RadioGroup>
            </FormControl>
          )}
        />
        {addingNewCreditCard && (
          <Stack alignItems="flex-end" sx={{ p: 2, width: '50%' }}>
            <img
              src={
                theme.palette.mode === 'light'
                  ? PoweredByStripe
                  : PoweredByStripeWhite
              }
              alt="Powered by Stripe"
              style={{ width: 120, height: 'auto' }}
            />
            <Grid container spacing={1} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, md: 12 }}>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? theme.palette.grey[400]
                        : theme.palette.grey[600],
                    ':hover': {
                      borderColor: (theme) =>
                        theme.palette.mode === 'light'
                          ? theme.palette.grey[700]
                          : theme.palette.grey[300],
                    },
                    borderRadius: 1,
                    p: 1.5,
                    mb: 2,
                  }}
                >
                  <CardElement
                    options={{
                      style: {
                        base: {
                          fontSize: '16px',
                          fontFamily: 'Roboto, sans-serif',
                          color: theme.palette.text.primary,
                          '::placeholder': {
                            color: theme.palette.text.secondary,
                          },
                        },
                      },
                    }}
                  />
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 12 }}>
                <Controller
                  name="newStripeCreditCard.account_holder_name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Cardholder Name"
                      error={!!errors.newStripeCreditCard?.account_holder_name}
                      helperText={
                        errors.newStripeCreditCard &&
                        errors.newStripeCreditCard.account_holder_name
                          ? errors.newStripeCreditCard.account_holder_name
                              .message
                          : ' '
                      }
                      size="small"
                      fullWidth
                      required
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{ xs: 12, md: 12 }}
                sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
              >
                <Controller
                  name={'newStripeCreditCard.address_line1'}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      inputProps={{
                        id: 'address',
                      }}
                      InputLabelProps={{
                        htmlFor: 'address',
                      }}
                      label="Street Address"
                      fullWidth
                      required
                      autoComplete="address-line1"
                      error={!!errors.newStripeCreditCard?.address_line1}
                      helperText={
                        errors.newStripeCreditCard?.address_line1
                          ? errors.newStripeCreditCard.address_line1.message
                          : ' '
                      }
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{ xs: 12, md: 6 }}
                sx={{ display: 'flex', flexDirection: 'column' }}
              >
                <Controller
                  name={'newStripeCreditCard.address_city'}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      inputProps={{
                        id: 'city',
                      }}
                      InputLabelProps={{
                        htmlFor: 'city',
                      }}
                      label="City"
                      fullWidth
                      required
                      autoComplete="address-level2"
                      error={!!errors.newStripeCreditCard?.address_city}
                      helperText={
                        errors.newStripeCreditCard?.address_city
                          ? errors.newStripeCreditCard.address_city.message
                          : ' '
                      }
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{ xs: 12, md: 6 }}
                sx={{ display: 'flex', flexDirection: 'column' }}
              >
                {selectedCountry === 'United States' ? (
                  <Controller
                    name="newStripeCreditCard.address_state"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        inputProps={{
                          id: 'state',
                        }}
                        InputLabelProps={{
                          htmlFor: 'state',
                        }}
                        label="State"
                        fullWidth
                        required
                        autoComplete="address-level1"
                        select
                        SelectProps={{
                          MenuProps: {
                            PaperProps: {
                              style: {
                                maxHeight: 450,
                              },
                            },
                          },
                        }}
                        error={!!errors.newStripeCreditCard?.address_state}
                        helperText={
                          errors.newStripeCreditCard?.address_state
                            ? errors.newStripeCreditCard.address_state.message
                            : ' '
                        }
                      >
                        {states.map((state) => (
                          <MenuItem key={state.value} value={state.value}>
                            {state.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                ) : (
                  <Controller
                    name="newStripeCreditCard.address_state"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        inputProps={{
                          id: 'state',
                        }}
                        InputLabelProps={{
                          htmlFor: 'state',
                        }}
                        label="Province"
                        fullWidth
                        autoComplete="address-level1"
                        error={!!errors.newStripeCreditCard?.address_state}
                        helperText={
                          errors.newStripeCreditCard?.address_state
                            ? errors.newStripeCreditCard.address_state.message
                            : ' '
                        }
                      />
                    )}
                  />
                )}
              </Grid>

              <Grid
                size={{ xs: 12 }}
                sx={{ display: 'flex', flexDirection: 'column' }}
              >
                <Controller
                  name={'newStripeCreditCard.address_country'}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);

                        if (
                          e.target.value === 'United States' ||
                          getValues('newStripeCreditCard.address_country') ===
                            'United States'
                        ) {
                          setValue('newStripeCreditCard.address_state', '');
                        }
                      }}
                      inputProps={{
                        id: 'country',
                      }}
                      InputLabelProps={{
                        htmlFor: 'country',
                      }}
                      label="Country/Region"
                      fullWidth
                      required
                      error={!!errors.newStripeCreditCard?.address_country}
                      helperText={
                        errors.newStripeCreditCard?.address_country
                          ? errors.newStripeCreditCard.address_country.message
                          : ' '
                      }
                      autoComplete="country"
                      select
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            style: {
                              maxHeight: 450,
                            },
                          },
                        },
                      }}
                    >
                      {countryOptions.map((country) => (
                        <MenuItem key={country} value={country}>
                          {country}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
            </Grid>
          </Stack>
        )}
        {stripePaymentType === 'PurchaseOrder' && (
          <Box sx={{ p: 2, width: '50%' }}>
            <Controller
              name={'purchaseOrderNumber'}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Purchase Order Number"
                  required
                  error={!!errors.purchaseOrderNumber}
                  helperText={errors.purchaseOrderNumber?.message || ' '}
                  fullWidth
                />
              )}
            />

            <Stack
              flexDirection="row"
              gap={2}
              alignItems="center"
              sx={{ mt: 1 }}
            >
              <Controller
                name={'purchaseOrderUpload'}
                control={control}
                render={({ field }) => (
                  <Button
                    component="label"
                    variant="contained"
                    startIcon={<CloudUpload />}
                    sx={{
                      width: 155,
                    }}
                  >
                    <VisuallyHiddenInput
                      type="file"
                      required
                      onChange={(event) => {
                        field.onChange(event.target.files?.[0] || null);
                        trigger();
                      }}
                    />
                    Upload PO
                  </Button>
                )}
              />

              <Typography
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {purchaseOrderUpload !== null
                  ? purchaseOrderUpload?.name
                  : 'No File Chosen'}
              </Typography>

              {purchaseOrderUpload !== null && (
                <Tooltip title="Remove file">
                  <IconButton
                    onClick={() => {
                      setValue('purchaseOrderUpload', null);
                    }}
                    sx={{ ml: 'auto' }}
                  >
                    <Close />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <FormHelperText
              sx={{ ml: 2, color: (theme) => theme.palette.error.main }}
            >
              {errors.purchaseOrderUpload?.message || ' '}
            </FormHelperText>
          </Box>
        )}
      </Stack>

      {showAchAgreement && (
        <Box sx={{ width: '100%', mt: 1, pl: 4, pr: 2 }}>
          <Alert severity="info" sx={{ alignItems: 'start' }}>
            By clicking [accept], you authorize Promevo to debit the bank
            account specified above for any amount owed for charges arising from
            your use of Promevo’s services and/or purchase of products from
            Promevo, pursuant to Promevo’s website and terms, until this
            authorization is revoked. You may amend or cancel this authorization
            at any time by providing notice to Promevo with 30 (thirty) days
            notice.
            <Controller
              name="achAgreementAccepted"
              control={control}
              render={({ field }) => (
                <FormControl
                  required
                  error={!!errors.achAgreementAccepted}
                  sx={{ display: 'block', mt: 1, pl: 0.5 }}
                >
                  <FormControlLabel
                    required
                    control={
                      <Checkbox
                        color="info"
                        checked={field.value || false}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="I accept the ACH authorization mandate."
                  />
                  {errors.achAgreementAccepted && (
                    <FormHelperText sx={{ ml: 0 }}>
                      {errors.achAgreementAccepted.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Alert>
        </Box>
      )}
    </Stack>
  );
};
